import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react';
import isArray from 'lodash/isArray';

import { Currencies } from '../../../../../../types';
import {
  FetchInvoicesCacheKeys,
  InvoiceCurrencyRateID,
  InvoiceID,
  InvoiceViewSettings
} from '../../../../invoicesTypes';
import {
  ProformaInvoiceFormGroupByMode,
  ProformaInvoiceFormViewMode
} from '../../../../../teams/components/forms/ProformaInvoiceForm/ProformaInvoiceForm.types';

import { CACHE_INVOICE_UPDATE_VIEW_SETTINGS_QUERY } from '../../../../queries/cacheInvoiceUpdateViewSettings.query';

import { usePreviousValue } from '../../../../../../common/hooks/usePreviousValue';
import { useCacheUpdateViewSettingsInvoice } from '../../../../hooks/useCacheUpdateViewSettingsInvoice';
import { useCacheInvoiceChangeCurrency } from '../../../../hooks/useCacheInvoiceChangeCurrency';

import { MultiSelectFieldChangeCallbackType } from '../../../../../../helpers/FormFields/MultiSelectField';
import { useInvoiceItemsViewCheckable } from '../useInvoiceItemsViewCheckable';
import { InvoiceViewItemsTableCheckedItemsType } from '../useInvoiceItemsViewContext';

interface InvoiceItemsViewHandlersOptions {
  invoice?: {
    id: InvoiceID;
    currencyRateId: InvoiceCurrencyRateID;
  };
  cacheKeys: FetchInvoicesCacheKeys;
  viewMode: ProformaInvoiceFormViewMode;
  groupBy: ProformaInvoiceFormGroupByMode;
  enableGrouping: boolean;
  groupedItemsLoading: boolean;
  setHasSubprojects: Dispatch<SetStateAction<boolean>>;
  flatItems: InvoiceViewItemsTableCheckedItemsType;
}

const isNewValue = (prevValue, newValue) =>
  prevValue !== undefined && prevValue !== newValue;

function useInvoiceItemsViewHandlers({
  invoice,
  cacheKeys,
  viewMode,
  groupBy,
  enableGrouping,
  groupedItemsLoading,
  setHasSubprojects,
  flatItems
}: InvoiceItemsViewHandlersOptions) {
  const prevViewMode = usePreviousValue(viewMode);
  const prevGroupBy = usePreviousValue(groupBy);
  const prevEnableGrouping = usePreviousValue(enableGrouping);

  const {
    updateViewSettingsInvoice,
    updateViewSettingsInvoiceLoading,
    updateViewSettingsInvoiceError
  } = useCacheUpdateViewSettingsInvoice({
    query: CACHE_INVOICE_UPDATE_VIEW_SETTINGS_QUERY,
    cacheKeys: cacheKeys
  });

  useEffect(() => {
    if (groupedItemsLoading && flatItems.length === 0) return;

    setHasSubprojects(
      flatItems.some(
        (item) =>
          'rootProjectNanoId' in item &&
          item.rootProjectNanoId &&
          item.rootProjectNanoId !== item.projectNanoId
      )
    );
  }, [flatItems, groupedItemsLoading, setHasSubprojects]);

  useEffect(() => {
    if (
      !isNewValue(prevViewMode, viewMode) &&
      !isNewValue(prevGroupBy, groupBy) &&
      !isNewValue(prevEnableGrouping, enableGrouping)
    ) {
      return;
    }

    const newInvoiceViewSettings: Partial<InvoiceViewSettings> = {
      viewMode,
      groupBy,
      enableGrouping
    };

    void updateViewSettingsInvoice({
      uuid: invoice?.id,
      viewSettings: newInvoiceViewSettings
    });
  }, [
    prevEnableGrouping,
    prevGroupBy,
    prevViewMode,
    viewMode,
    groupBy,
    enableGrouping,
    invoice?.id,
    updateViewSettingsInvoice
  ]);

  const { changeCurrency, changeCurrencyLoading, changeCurrencyErrorMessage } =
    useCacheInvoiceChangeCurrency({
      cacheKeys
    });

  const [changeCurrencyDebouncedLoading, setChangeCurrencyDebouncedLoading] =
    useState(changeCurrencyLoading);

  useEffect(() => {
    if (!changeCurrencyLoading) {
      setTimeout(() => setChangeCurrencyDebouncedLoading(false), 300);
      return;
    }

    setChangeCurrencyDebouncedLoading(changeCurrencyLoading);
  }, [changeCurrencyLoading]);

  const handleChangeCurrency = useCallback<MultiSelectFieldChangeCallbackType>(
    (data) =>
      changeCurrency({
        uuid: invoice?.id,
        currencyRateId: invoice?.currencyRateId,
        selectedCurrency: (isArray(data) ? data[0] : data)?.value as Currencies
      }),
    [changeCurrency, invoice?.currencyRateId, invoice?.id]
  );

  return {
    handleChangeCurrency,
    updateViewSettingsInvoiceLoading,
    updateViewSettingsInvoiceError,
    changeCurrencyLoading: changeCurrencyDebouncedLoading,
    changeCurrencyError: changeCurrencyErrorMessage
  };
}

export default useInvoiceItemsViewHandlers;
